/**
 * Fetches the latest Medium blog posts for a given username.
 * Uses rss2json.com to convert Medium's RSS feed to JSON (free, no auth needed).
 * @param {string} username - Medium username
 * @param {number} maxPosts - Maximum number of posts to fetch (default: 3)
 * @returns {Promise<Array>} Array of blog post objects
 */
export const fetchMediumPosts = async (username = '@pasinduperera10', maxPosts = 3) => {
  try {
    // Remove @ if present for the URL
    const cleanUsername = username.replace('@', '');
    const rssUrl = `https://medium.com/feed/@${cleanUsername}`;
    
    // Use rss2json.com to convert RSS to JSON (free tier)
    const response = await fetch(
      `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(rssUrl)}`
    );

    if (!response.ok) {
      throw new Error(`RSS API error: ${response.status}`);
    }

    const data = await response.json();

    if (data.status !== 'ok' || !data.items) {
      // Fallback: try a different approach or return empty
      console.warn('Medium RSS feed returned no items. The feed may be empty.');
      return [];
    }

    return data.items.slice(0, maxPosts).map(item => ({
      title: item.title,
      link: item.link,
      description: stripHtml(item.description).substring(0, 200) + '...',
      thumbnail: item.thumbnail || '',
      pubDate: formatDate(item.pubDate),
      categories: item.categories || [],
      author: item.author,
    }));
  } catch (error) {
    console.error('Error fetching Medium posts:', error);
    return [];
  }
};

/**
 * Strips HTML tags from a string.
 * @param {string} html - HTML string
 * @returns {string} Plain text
 */
const stripHtml = (html) => {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

/**
 * Formats a date string into a readable format.
 * @param {string} dateStr - Date string
 * @returns {string} Formatted date
 */
const formatDate = (dateStr) => {
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
};